import { useParams, useNavigate } from "react-router-dom";
import { AccountLayout } from "../components/AccountLayout";
import { useState, useEffect } from "react";
import Cover from "../components/BookCover";
import { fetchOrderById } from "../api";
import { getStoredAuthToken } from "../authStorage";

const TIMELINE_STEPS = [
    { label: "Order Placed", icon: "📋" },
    { label: "Confirmed", icon: "✅" },
    { label: "Shipped", icon: "🚚" },
    { label: "Out for Delivery", icon: "📬" },
    { label: "Delivered", icon: "🎉" },
];

function getActiveStep(status) {
    const map = { "Processing": 1, "Confirmed": 2, "Shipped": 3, "Out for Delivery": 4, "Delivered": 5 };
    return map[status] || 1;
}

export default function OrderDetailPage({ user, onLogout }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const token = getStoredAuthToken();
                if (!token) {
                    navigate("/signin");
                    return;
                }

                const data = await fetchOrderById(id, token);
                setOrder(data);
            } catch (error) {
                console.error("Error fetching order:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [id, navigate]);

    if (loading) return (
        <AccountLayout user={user} onLogout={onLogout}>
            <div style={{ textAlign: "center", padding: "80px 0", color: "var(--text3)" }}>Loading order details...</div>
        </AccountLayout>
    );

    if (!order) return (
        <AccountLayout user={user} onLogout={onLogout}>
            <div style={{ textAlign: "center", padding: "80px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
                <h2 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 800, marginBottom: 8 }}>Order Not Found</h2>
                <p style={{ color: "var(--text3)", marginBottom: 24 }}>We couldn't find this order.</p>
                <button className="btn btn-forest" onClick={() => navigate("/orders")}>← Back to Orders</button>
            </div>
        </AccountLayout>
    );

    const computedStatus = order.isDelivered ? "Delivered" : "Processing";
    const activeStep = getActiveStep(computedStatus);
    const orderCode = String(order._id || order.id || "").slice(-8);

    return (
        <AccountLayout user={user} onLogout={onLogout}>
            <div className="order-detail-page" style={{ animation: "fadeUp 0.4s both" }}>
                <style>{`
                    .timeline-container {
                        display: flex;
                        align-items: flex-start;
                        overflow-x: auto;
                        padding-bottom: 12px;
                        -webkit-overflow-scrolling: touch;
                    }
                    .timeline-step {
                        flex: 1;
                        min-width: 80px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        position: relative;
                    }
                    .info-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    }
                    @media (max-width: 768px) {
                        .order-header-flex {
                            flex-direction: column !important;
                            align-items: flex-start !important;
                            gap: 16px !important;
                        }
                        .info-grid {
                            grid-template-columns: 1fr !important;
                        }
                        .timeline-container {
                            justify-content: flex-start !important;
                        }
                    }
                    @media (max-width: 480px) {
                        .order-card-padding { padding: 18px !important; }
                        .order-item-title { font-size: 13px !important; }
                    }
                `}</style>
                <button onClick={() => navigate("/orders")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text3)", fontWeight: 600, marginBottom: 16, fontFamily: "'Nunito Sans',sans-serif" }}>← Back to Orders</button>
                <div className="order-header-flex" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                    <div>
                        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 800, color: "var(--ink)", marginBottom: 4 }}>Order #{orderCode}</h2>
                        <span style={{ fontSize: 13, color: "var(--text3)" }}>Placed on {new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                    <span style={{ display: "inline-flex", alignItems: "center", padding: "6px 16px", borderRadius: 50, fontSize: 12, fontWeight: 800, background: order.isPaid ? "#10B98118" : "#F59E0B18", color: order.isPaid ? "#10B981" : "#F59E0B" }}>{computedStatus}</span>
                </div>

                <div className="order-card-padding" style={{ background: "var(--paper)", borderRadius: 18, border: "1.5px solid var(--border)", padding: 28, marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text2)", letterSpacing: "0.04em", marginBottom: 20 }}>ORDER TRACKING</div>
                    <div className="timeline-container">
                        {TIMELINE_STEPS.map((step, i) => {
                            const done = i < activeStep;
                            const current = i === activeStep - 1;
                            return (
                                <div key={i} className="timeline-step">
                                    {i > 0 && <div style={{ position: "absolute", top: 18, right: "50%", width: "100%", height: 3, background: done ? "var(--forest)" : "var(--border)", zIndex: 0 }} />}
                                    <div style={{
                                        width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, zIndex: 1,
                                        background: done ? "var(--forest)" : current ? "var(--gold)" : "var(--cream2)",
                                        border: `2px solid ${done ? "var(--forest)" : current ? "var(--gold)" : "var(--border)"}`,
                                    }}>
                                        {done ? <span style={{ color: "white", fontSize: 14 }}>✓</span> : step.icon}
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: done || current ? 700 : 500, color: done ? "var(--forest)" : current ? "var(--gold)" : "var(--text3)", marginTop: 8, textAlign: "center", whiteSpace: "nowrap" }}>{step.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="order-card-padding" style={{ background: "var(--paper)", borderRadius: 18, border: "1.5px solid var(--border)", padding: 28, marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text2)", letterSpacing: "0.04em", marginBottom: 18 }}>ITEMS ({order.orderItems.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {order.orderItems.map((item, idx) => (
                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <Cover book={item} w={48} h={66} shadow={false} />
                                <div style={{ flex: 1 }}>
                                    <div className="order-item-title" style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)" }}>{item.title}</div>
                                    <div style={{ fontSize: 12, color: "var(--text3)" }}>Qty: {item.qty}</div>
                                </div>
                                <span style={{ fontWeight: 800, color: "var(--forest)", fontSize: 14 }}>{fmtINR(item.price)}</span>
                            </div>
                        ))}
                    </div>
                    <div style={{ borderTop: "1.5px dashed var(--border)", marginTop: 18, paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "var(--text3)" }}>Delivery: FREE</span>
                        <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 800, fontSize: 18, color: "var(--forest)" }}>Total: {fmtINR(order.totalPrice)}</div>
                    </div>
                </div>

                <div className="info-grid">
                    <div className="order-card-padding" style={{ background: "var(--paper)", borderRadius: 18, border: "1.5px solid var(--border)", padding: 24 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text2)", letterSpacing: "0.04em", marginBottom: 12 }}>DELIVERY ADDRESS</div>
                        <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.7 }}>
                            {user?.name || "User"}<br />
                            {order.shippingAddress?.street}<br />
                            {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.zipCode}
                        </div>
                    </div>
                    <div className="order-card-padding" style={{ background: "var(--paper)", borderRadius: 18, border: "1.5px solid var(--border)", padding: 24 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text2)", letterSpacing: "0.04em", marginBottom: 12 }}>PAYMENT METHOD</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
                            <span style={{ fontSize: 22 }}>💳</span> {order.paymentMethod?.toUpperCase()}
                        </div>
                    </div>
                </div>
            </div>
        </AccountLayout>
    );
}
